import { useMemo } from 'react';
import {
  computePerspectiveComparison,
  fmt,
  fmtMoney,
  type SeparationState,
} from '@integrate/core';
import { StatTile } from '../ui/StatTile.js';

/*
  The three perspectives of this scenario side by side, and how each as-is group divides between them.

  THE ROW IS THE AS-IS GROUP and the columns say where its people went — the same reading as the coverage
  matrix, and the only one under which three columns describe one population. The MONEY resolves through
  its own column, though, because a column IS a perspective: a carve-out salary override belongs in the
  carve-out total and not in the as-is one.

  THE TABLE ACCOUNTS FOR THE CARDS. The app built its group list with `.filter(Boolean)` and skipped
  blank keys inside the aggregation, so people with no value at that level vanished from every column
  while the cards above counted them — 500 people over a table summing to 488, with nothing on the page
  explaining the twelve. Blanks fold to "(No <label>)" here, and the totals row says the arithmetic
  closes.

  ONE SCENARIO, the one open — the same call the phase bridge made. The app's version has a scenario
  picker, and the endpoint could serve it (`/design?scenarioId=`), but this client holds ONE design at a
  time; a picker that silently compared the design in memory against a name would be worse than no
  picker. That is the transport work, and it is recorded rather than faked.
*/

export function PerspectivePane({
  state,
  scenarioName,
  levelKey,
  onLevelKey,
}: {
  state: SeparationState;
  scenarioName: string;
  levelKey: string | null;
  onLevelKey(next: string | null): void;
}): JSX.Element {
  const C = useMemo(
    () => computePerspectiveComparison(state, { levelKey }),
    [state, levelKey],
  );

  return (
    <div data-testid="perspective-pane" data-level-key={levelKey ?? ''}>
      <p className="muted">
        Scenario <strong>{scenarioName}</strong>, read three ways. As-is is the organisation that was
        imported; RemainCo and Carve-out are what this design makes of it. Planned hires do not exist
        as-is, so they appear only in the two design columns.
      </p>

      <div className="stat-grid" data-testid="perspective-cards">
        {C.summaries.map((s) => (
          <StatTile
            key={s.key}
            testId={`perspective-card-${s.key}`}
            attrs={{ 'data-hc': s.hc, 'data-salary': s.salary }}
            label={s.label}
            value={fmt(s.hc)}
            sub={
              <>
                {fmtMoney(s.salary)} · {fmt(s.fte, 1)} FTE
                {s.buildUps > 0 && <> · {fmt(s.buildUps)} planned</>}
              </>
            }
          >
            {(s.openPositions > 0 || s.inactive > 0) && (
              <div className="sub muted">
                {/* Beside the number, as the group cards do — not silently dropped. */}
                {s.openPositions > 0 && <>+{fmt(s.openPositions)} open</>}
                {s.openPositions > 0 && s.inactive > 0 && <>, </>}
                {s.inactive > 0 && <>+{fmt(s.inactive)} inactive</>}
              </div>
            )}
          </StatTile>
        ))}
      </div>

      <div className="row">
        <span className="muted small">Broken down by</span>
        <select
          data-testid="perspective-level"
          value={levelKey ?? ''}
          onChange={(e) => onLevelKey(e.target.value || null)}
        >
          <option value="">Nothing — the cards only</option>
          {state.groupingLevels.map((l) => (
            <option key={l.key} value={l.key}>
              {l.label}
            </option>
          ))}
        </select>
      </div>

      {C.rows.length === 0 ? (
        <p className="muted" data-testid="perspective-no-rows">
          {state.groupingLevels.length === 0
            ? 'This project has no grouping level to break the comparison down by.'
            : 'Choose a level above to see how each group divides between the three.'}
        </p>
      ) : (
        <table className="data-table" data-testid="perspective-table">
          <thead>
            <tr>
              <th>{C.levelLabel}</th>
              <th className="num">As-is HC</th>
              <th className="num">RemainCo HC</th>
              <th className="num">Carve-out HC</th>
              <th className="num">No longer req.</th>
              <th className="num">RemainCo cost</th>
              <th className="num">Carve-out cost</th>
            </tr>
          </thead>
          <tbody>
            {C.rows.map((r, i) => (
              <tr
                key={r.key}
                data-testid={`perspective-row-${i}`}
                data-key={r.key}
                data-asis={r.asis.hc}
                data-remain={r.remain.hc}
                data-carve={r.carveOut.hc}
                data-nlr={r.noLonger.hc}
              >
                <td>{r.key}</td>
                <td className="num">{fmt(r.asis.hc)}</td>
                <td className="num">{fmt(r.remain.hc)}</td>
                <td className="num">{fmt(r.carveOut.hc)}</td>
                <td className="num">{fmt(r.noLonger.hc)}</td>
                <td className="num">{fmtMoney(r.remain.salary)}</td>
                <td className="num">{fmtMoney(r.carveOut.salary)}</td>
              </tr>
            ))}
            {/*
              The totals row is the point. Its As-is figure is the As-is card, which is what the app's
              version could not say — and could not have been made to say without a "(No …)" row.
            */}
            <tr style={{ fontWeight: 700 }} data-testid="perspective-total" data-asis={C.totals.asis}>
              <td>Total</td>
              <td className="num">{fmt(C.totals.asis)}</td>
              <td className="num">{fmt(C.totals.remain)}</td>
              <td className="num">{fmt(C.totals.carveOut)}</td>
              <td className="num">{fmt(C.totals.noLonger)}</td>
              <td className="num" />
              <td className="num" />
            </tr>
          </tbody>
        </table>
      )}

      <p className="muted small">
        Rows are as-is groups, so a person stays on their own row whichever column counts them, and every
        column adds up to the card above it. Cost is raw annual salary, resolved through its own column —
        a per-scenario salary edit moves the column it belongs to and no other. Open positions, inactive
        people and attrition leavers are outside every headcount, and named on the cards.
      </p>
    </div>
  );
}
